import type { SupervisedActionRequest } from './types'

interface RadsuiteDevHelper {
  help: () => void
  listCommands: () => string[]
  request: (overrides?: Partial<SupervisedActionRequest>) => string
  publishSkill: (overrides?: Partial<SupervisedActionRequest>) => string
  listSkills: (overrides?: Partial<SupervisedActionRequest>) => string
}

function isDevChannelEnabled(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  const devChannel = [...params.entries()]
    .find(([key]) => key.toLowerCase() === 'devchannel')?.[1]
  return ['1', '2', '3'].includes(devChannel || '')
}

function buildRequest(overrides: Partial<SupervisedActionRequest> = {}): SupervisedActionRequest {
  return {
    requestId: crypto.randomUUID(),
    transport: 'postmessage',
    requesterType: 'iframe',
    kind: 'zulu.api',
    action: 'skills.publish',
    accountId: 'dev-account',
    channelId: 'devchannel-local-1',
    payload: {
      skillName: 'demo-skill',
      path: '.rad/skills/demo-skill',
      files: {
        'SKILL.md': '# Demo Skill\n\nThis is a dev helper test.'
      }
    },
    metadata: {
      requesterName: 'Dev Console Helper',
      origin: window.location.origin,
      riskHint: 'medium',
      fallbackToHumanModal: true
    },
    ...overrides
  }
}

function printHelp() {
  console.info('%cRADSuite Dev Helper Enabled', 'font-size: 16px; font-weight: bold; color: #2563eb;')
  console.info('%cUse window.radsuitedev to trigger supervisor bridge requests from DevTools.', 'color: #475569;')
  console.table([
    {
      command: 'radsuitedev.help()',
      description: 'Show this help output again.'
    },
    {
      command: 'radsuitedev.listCommands()',
      description: 'Return the available helper commands.'
    },
    {
      command: 'radsuitedev.publishSkill()',
      description: 'Open the supervisor modal for a sample skills.publish request.'
    },
    {
      command: 'radsuitedev.listSkills()',
      description: 'Send a low-risk skills.list request through the bridge.'
    },
    {
      command: 'radsuitedev.request({ action, kind, payload, ... })',
      description: 'Send a custom supervised action request.'
    }
  ])
}

export function initializeSupervisorDevHelper() {
  if (typeof window === 'undefined' || !isDevChannelEnabled()) {
    return
  }

  const helper: RadsuiteDevHelper = {
    help() {
      printHelp()
    },
    listCommands() {
      return [
        'radsuitedev.help()',
        'radsuitedev.listCommands()',
        'radsuitedev.publishSkill()',
        'radsuitedev.listSkills()',
        'radsuitedev.request({ action, kind, payload, ... })'
      ]
    },
    request(overrides: Partial<SupervisedActionRequest> = {}) {
      const request = buildRequest(overrides)
      window.postMessage({
        type: 'radsuite.supervisor.request',
        request
      }, window.location.origin)
      return request.requestId
    },
    publishSkill(overrides: Partial<SupervisedActionRequest> = {}) {
      return helper.request({
        action: 'skills.publish',
        kind: 'zulu.api',
        ...overrides
      })
    },
    listSkills(overrides: Partial<SupervisedActionRequest> = {}) {
      return helper.request({
        action: 'skills.list',
        kind: 'zulu.api',
        payload: {},
        ...overrides
      })
    }
  }

  ;(window as Window & { radsuitedev?: RadsuiteDevHelper }).radsuitedev = helper
  printHelp()
}